import type { FieldMetadata, FieldValue } from '../../data/client';
import { FieldDisplay } from '../../fields/display/FieldDisplay';
import { FieldProvider } from '../../fields/FieldContext';

/**
 * A value rendered the way the whole app renders it, with no way to change it.
 *
 * The record page shows values in four places: the current one in a field row, a
 * before/after pair in the timeline, the value an automated writer attempted, and
 * the value that is waiting for approval. All four go through here, so the page
 * never stringifies a value itself - a MULTI_SELECT would become
 * "fitness,lifestyle" and a CURRENCY would lose its code, which is exactly the
 * drift the prop-less field components exist to prevent.
 */
export interface ReadOnlyFieldValueProps {
  readonly objectId: string;
  readonly recordId: string;
  readonly field: FieldMetadata;
  readonly value: FieldValue;
  /** INV-5, only meaningful for the LIVE value of a field, never for history. */
  readonly humanOwned?: boolean | undefined;
}

/** Displays ignore onChange; a no-op keeps the context shape honest. */
const IGNORE_CHANGE = () => undefined;

export function ReadOnlyFieldValue({
  objectId,
  recordId,
  field,
  value,
  humanOwned = false,
}: ReadOnlyFieldValueProps) {
  return (
    <FieldProvider
      value={{
        objectId,
        recordId,
        field,
        value,
        onChange: IGNORE_CHANGE,
        readOnly: true,
        humanOwned,
        /**
         * F1: on a READ surface a select value is text with a colour dot, never a tinted
         * capsule. This is the one place the flag is set, so the band, the inspector
         * rows, the phone's grouped lists and the timeline's before/after pairs cannot
         * disagree about what a stored option looks like.
         */
        tagAsDot: true,
      }}
    >
      <FieldDisplay />
    </FieldProvider>
  );
}
